import React, { useRef } from 'react';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { Field, useField } from 'formik';
import { Attachment01 } from '@kula-ai/icons/outline/Attachment01';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { colors } from '@/theme/foundations/colors';
import { useFileValidation } from '@/hooks/useFileValidation';
import { useMemo } from 'react';

const BYTES_TO_KB = 1024;
const KB_TO_MB = 1024;
//add 0.1 to the limit to avoid rounding errors(Less than 15MB)
const TOTAL_SIZE_LIMIT_MB = 14.9;

export const FileAttachment = ({
  fileTypes = '',
  initialValue = [],
  attachmentKey = 'attachments',
  isDisabled = false,
  disabledMessage,
  onAttachmentChange: onAttachmentChangeCallback,
  tooltipMessage,
  totalSizeLimitMB = TOTAL_SIZE_LIMIT_MB,
}) => {
  const attachFileRef = useRef();
  const toast = useShowToast();
  const { ats_enabled } = useKFlags();
  const { handleAttachmentValidation } = useFileValidation();

  // eslint-disable-next-line no-unused-vars
  const [_field, { value: attachmentValue }, helpers] = useField({
    name: attachmentKey,
  });
  // eslint-disable-next-line
  const [_hiddenField, _hiddenMeta, hiddenHelpers] = useField({
    name: `${attachmentKey}_updated`,
  });

  const onAttachment = () => {
    if (isDisabled) return;
    attachFileRef?.current?.click();
  };

  const onAttachmentChange = async (e) => {
    document.activeElement.blur();

    const fileArr = [...(attachmentValue || [])];
    const files = e?.target?.files;

    if (!files?.length) return;

    // Validate file contents
    const isValid = await handleAttachmentValidation({
      files,
    });
    if (!isValid) {
      e.target.value = '';
      return;
    }

    // Check total size limit (15MB)
    const existingFilesSize = fileArr.reduce(
      (total, file) => total + (file.size || 0),
      0
    );
    const newFilesSize = Array.from(files).reduce(
      (total, file) => total + file.size,
      0
    );
    const totalSize = existingFilesSize + newFilesSize;

    if (totalSize / BYTES_TO_KB / KB_TO_MB > totalSizeLimitMB) {
      toast({
        title: 'Total file size exceeds limit',
        description: `The total size of all attachments should be less than ${Math.round(
          totalSizeLimitMB
        )} MB. Please remove some files or choose smaller files.`,
        id: 'attachment-total-size-error',
        position: 'bottom-left',
        status: 'error',
        isClosable: true,
      });
      e.target.value = '';
      return;
    }

    // Add the valid file(s)
    if (files.length === 1) {
      fileArr.push(files[0]);
    } else {
      // Handle multiple files if needed
      for (let i = 0; i < files.length; i++) {
        fileArr.push(files[i]);
      }
    }

    hiddenHelpers.setValue(true);
    helpers.setValue(fileArr);

    // Call the callback if provided
    if (
      onAttachmentChangeCallback &&
      typeof onAttachmentChangeCallback === 'function'
    ) {
      onAttachmentChangeCallback(fileArr);
    }

    e.target.value = '';
  };

  const defaultMsg = `Upload files less than 15 MB in size.`;

  // Use custom tooltip message if provided, otherwise use default logic
  const getTooltipLabel = useMemo(() => {
    if (tooltipMessage) return tooltipMessage;
    return isDisabled ? disabledMessage || defaultMsg : defaultMsg;
  }, [tooltipMessage, isDisabled, disabledMessage, defaultMsg]);

  return (
    <Flex alignItems="center">
      <input
        id="email-attachments"
        type="file"
        ref={attachFileRef}
        onInput={(e) => onAttachmentChange(e)}
        style={{ visibility: 'hidden', height: 0, width: 0 }}
        accept={fileTypes}
        isDisabled={isDisabled}
      />
      <Field name={attachmentKey} type="hidden" />
      <Field name={`${attachmentKey}_updated`} type="hidden" value="false" />
      {/* Dynamic tooltip based on form data or default message */}
      <Tooltip
        label={getTooltipLabel}
        arrowSize={8}
        hasArrow
        placement="top"
        variant={ats_enabled && 'ats-primary'}
        borderRadius="4px"
      >
        <Box
          role="button"
          onClick={onAttachment}
          borderRadius="6px"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          p="4px"
          opacity={isDisabled ? 0.5 : 1}
          disabled={isDisabled ? isDisabled : attachmentValue?.length >= 3}
          data-test-id="editor-toolbar-attachment-button"
        >
          <Attachment01 color={colors['secondary-700']} />
        </Box>
      </Tooltip>
    </Flex>
  );
};
